import { createSlice } from '@reduxjs/toolkit'
import {
  CertificateGroupEnum,
  getAttributeUniqueName,
} from '../../utils/constants/certificate.constants'
const initialState = {
  copyPartyInformation: false,
  value: {
    selectedProjectDetails: null,
    lineDetails: [],
    orderLineNumbers: [],
    selectedOrderLineNumber: null,
  },
  serviceRequestDetails: {
    serviceRequestNumber: null,
  },
  toggleServiceRequest: false,
  selectedServiceRequest: null,
  certDetails: {
    formValues: null,
    flatAttributeList: null,
    standardOptions: null,
    secondaryStandardOptions: null,
    standardValue: null,
    secondaryStandardValue: null,
    standardSearch: null,
    productCategoryOptions: null,
    productCategoryStandards: null,
    selectedProductCategory: null,
    isCertificateRecommended: true,
    isSkipCoDecisioning: false,
    tempStandardOptions: null,
    isTempStandardOptionsLoaded: false,
    tempSecondaryStandardOptions: null,
    hasIssueDate: false,
  },
  productDetails: {
    modelList: null,
    remarks: null,
    attachments: null,
  },
  modelDetails: {
    partySiteNumber: null,
    productType: null,
    isDisable: false,
  },
  modelList: [],
  modelsProductStandardsUpdates: [],
  certificateNumber: null,
  partySiteNumber: null,
  autoTimestampTask: [],
  isCertNumberOnMaxlength: false,
  certificateScheme: null,
  hasExistingModels: false,
  productOwner: null,
  productOwnerSearch: null,
  hasEmptyProductStandardRow: true,
  hasCertificateProductStandard: true,
  attachmentUpdatedList: null,
  schemeEcmType: null,
  isCo: false,
  isRPAttachmentValid: true,
  isModelOnEdit: false,
  copyState: {
    certificateScheme: false,
    certificateNumber: false,
    models: false,
    partyInformation: false,
  },
  isAddNewCertFromParams: false,
  assetDocuments: [],
  certificateAction: {
    isWithdraw: false,
  },
  productRatings: [],
}

export const createCertificateSlice = createSlice({
  name: 'createCertificate',
  initialState,
  reducers: {
    updateCopyPartyInformation: (state, action) => {
      state.copyPartyInformation = action.payload
    },
    updateSelectedProjectDetails: (state, action) => {
      state.value.selectedProjectDetails = action.payload
    },
    updateLineDetails: (state, action) => {
      state.value.lineDetails = action.payload
    },
    updateOrderLineNumbers: (state, action) => {
      state.value.orderLineNumbers = action.payload
    },
    updateSelectedOrderLineNumber: (state, action) => {
      state.value.selectedOrderLineNumber = action.payload
    },
    updateCertDetailsFormValues: (state, action) => {
      state.certDetails.formValues = action.payload
    },
    updateCertDetailsFlatAttrList: (state, action) => {
      state.certDetails.flatAttributeList = action.payload
    },
    updateProdDetailsModelList: (state, action) => {
      state.productDetails.modelList = action.payload
    },
    updateProdDetailsRemarks: (state, action) => {
      state.productDetails.remarks = action.payload
    },
    updateProdDetailsAttachments: (state, action) => {
      state.productDetails.attachments = action.payload
    },
    updateProductCategoryOptions: (state, action) => {
      state.certDetails.productCategoryOptions = action.payload
    },
    updateStandardValues: (state, action) => {
      state.certDetails.standardValue = action.payload
    },
    updateSecondaryStandardValues: (state, action) => {
      state.certDetails.secondaryStandardValue = action.payload
    },
    updateStandardSearch: (state, action) => {
      state.certDetails.standardSearch = action.payload
    },
    updateStandardOptions: (state, action) => {
      state.certDetails.standardOptions = action.payload
    },
    updateSecondaryStandardOptions: (state, action) => {
      state.certDetails.secondaryStandardOptions = action.payload
    },
    updateProductCategoryStandards: (state, action) => {
      state.certDetails.productCategoryStandards = action.payload
    },
    updateModelDetails: (state, action) => {
      state.modelDetails = action.payload
    },
    updateCertificateNumber: (state, action) => {
      state.certificateNumber = action.payload
    },
    updatePartySiteNumber: (state, action) => {
      state.partySiteNumber = action.payload
    },
    updateAutoTimestampTask: (state, action) => {
      state.autoTimestampTask = action.payload
    },
    updateIsCertNumberOnMaxlength: (state, action) => {
      state.isCertNumberOnMaxlength = action.payload
    },
    updateCertificateScheme: (state, action) => {
      state.certificateScheme = action.payload
    },
    updateModelList: (state, action) => {
      state.modelList = action.payload
    },
    patchModelListItem: (state, action) => {
      state.modelList = state.modelList.map(model => ({
        ...model,
        ...(model.assetId === action.payload.assetId ? action.payload.patch : {}),
      }))
    },
    updateHasExistingModels: (state, action) => {
      state.hasExistingModels = action.payload
    },
    updateProductOwner: (state, action) => {
      state.productOwner = action.payload
    },
    updateProductOwnerSearch: (state, action) => {
      state.productOwnerSearch = action.payload
    },
    appendProductStandards: (state, action) => {
      const newProductStandards =
        state.certDetails.formValues?.[
          getAttributeUniqueName(CertificateGroupEnum.Certificate, 'standard')
        ] || []

      action.payload?.forEach(code => {
        if (!newProductStandards.includes(code)) newProductStandards.push(code)
      })

      state.certDetails.formValues = {
        ...state.certDetails.formValues,
        [getAttributeUniqueName(CertificateGroupEnum.Certificate, 'standard')]: [
          ...new Set(newProductStandards),
        ],
      }
    },
    setModelsProductStandardsUpdates: (state, action) => {
      state.modelsProductStandardsUpdates = action.payload
    },
    updateHasEmptyProductStandardRow: (state, action) => {
      state.hasEmptyProductStandardRow = action.payload
    },
    updateHasCertificateProductStandard: (state, action) => {
      state.hasCertificateProductStandard = action.payload
    },
    updateAttachmentList: (state, action) => {
      state.attachmentUpdatedList = action.payload
    },
    updateSchemeEcmType: (state, action) => {
      state.schemeEcmType = action.payload
    },
    updateSelectedProductCategory: (state, action) => {
      state.certDetails.selectedProductCategory = action.payload
    },
    updateRecommendationStatus: (state, action) => {
      state.certDetails.isCertificateRecommended = action.payload
    },
    updateSkipCoDecisioning: (state, action) => {
      state.certDetails.isSkipCoDecisioning = action.payload
    },
    updateIsCo: (state, action) => {
      state.certDetails.isCo = action.payload
    },
    updateTempStandardOptions: (state, action) => {
      state.certDetails.tempStandardOptions = action.payload
    },
    updateIsTempStandardOptionLoaded: (state, action) => {
      state.certDetails.isTempStandardOptionsLoaded = action.payload
    },
    updateTempSecondaryStandardOptions: (state, action) => {
      state.certDetails.tempSecondaryStandardOptions = action.payload
    },
    updateHasIssueDate: (state, action) => {
      state.certDetails.hasIssueDate = action.payload
    },
    updateIsRPAttachmentValid: (state, action) => {
      state.isRPAttachmentValid = action.payload
    },
    updateIsModelOnEdit: (state, action) => {
      state.isModelOnEdit = action.payload
    },
    updateCopyState: (state, action) => {
      state.copyState = action.payload
    },
    updateIsAddNewCertFromParams: (state, action) => {
      state.isAddNewCertFromParams = action.payload
    },
    updateAssetDocuments: (state, action) => {
      const { assetId, column, fileName, rawBase64, docId } = action.payload

      const current = state.assetDocuments ?? []

      const existingIndex = current.findIndex(
        doc =>
          doc.data.entityId === assetId &&
          doc.data.documents?.some(d => d.docType === column && d.action === 'add')
      )

      if (existingIndex !== -1) {
        const doc = current[existingIndex]
        const documents = doc.data.documents ?? []

        const targetDoc = documents.find(d => d.docType === column && d.action === 'add')

        if (targetDoc?.docId) {
          const updatedDocuments = documents.map(d =>
            d.docType === column && d.action === 'add' ? { ...d, action: 'delete' } : d
          )

          const updatedObject = {
            ...doc,
            data: {
              ...doc.data,
              documents: updatedDocuments,
            },
          }

          const newObject = {
            data: {
              entityType: 'assets',
              entityId: assetId,
              documents: [
                {
                  action: 'add',
                  docType: column,
                  docName: fileName,
                  docData: rawBase64,
                  docId: '',
                },
              ],
            },
          }

          state.assetDocuments = [
            ...current.slice(0, existingIndex),
            updatedObject,
            ...current.slice(existingIndex + 1),
            newObject,
          ]
          return
        }

        state.assetDocuments = current.map(d => {
          if (d.data.entityId === assetId) {
            const updatedDocs = d.data.documents.map(docItem =>
              docItem.docType === column && docItem.action === 'add'
                ? {
                    ...docItem,
                    docName: fileName,
                    docData: rawBase64,
                  }
                : docItem
            )
            return {
              ...d,
              data: {
                ...d.data,
                documents: updatedDocs,
              },
            }
          }
          return d
        })
        return
      }

      state.assetDocuments = [
        ...current,
        {
          data: {
            entityType: 'assets',
            entityId: assetId,
            documents: [
              {
                action: 'add',
                docType: column,
                docName: fileName,
                docData: rawBase64,
                docId,
              },
            ],
          },
        },
      ]
    },
    clearAssetDocuments: state => {
      state.assetDocuments = []
    },
    deleteAssetDocuments: (state, action) => {
      state.assetDocuments = action.payload
    },
    removeAssetDocument: (state, action) => {
      const { assetId, column } = action.payload

      if (!state.assetDocuments || state.assetDocuments.length === 0) {
        return
      }

      state.assetDocuments = state.assetDocuments.flatMap(doc => {
        if (doc.data.entityId !== assetId) {
          return [doc]
        }

        const documents = doc.data.documents ?? []
        const targetDoc = documents.find(d => d.docType === column)

        if (!targetDoc) {
          return [doc]
        }

        if (!targetDoc.docId) {
          return []
        }

        const filteredDocs = documents.filter(d => !(d.docType === column && d.action === 'add'))

        const updatedDocs = [
          ...filteredDocs,
          {
            action: 'delete',
            docType: column,
            docData: '',
            docId: targetDoc.docId,
            docName: targetDoc.docName,
          },
        ]

        return [
          {
            ...doc,
            data: {
              ...doc.data,
              documents: updatedDocs,
            },
          },
        ]
      })
    },
    applyAssetDocumentSaveResults: (state, action) => {
      const { successItems } = action.payload

      state.assetDocuments = state.assetDocuments
        .map(doc => {
          if (!successItems.some(s => s.entityId === doc.data.entityId)) {
            return doc
          }

          const updatedDocs = doc.data.documents.map(d => {
            const match = successItems
              .filter(s => s.action !== 'delete')
              .find(s => s.entityId === doc.data.entityId && d.action === 'add')

            return match
              ? {
                  docId: match.docId,
                  docName: match.docName,
                  docType: match.docType,
                  action: match.action,
                  docData: '',
                }
              : d
          })

          return {
            ...doc,
            data: {
              ...doc.data,
              documents: updatedDocs,
            },
          }
        })
        .filter(
          doc =>
            doc.data.documents.length > 0 && !doc.data.documents.some(d => d.action === 'delete')
        )
    },
    updateCertificateAction: (state, action) => {
      state.certificateAction = action.payload
    },
    updateServiceRequestNumber: (state, action) => {
      state.serviceRequestDetails.serviceRequestNumber = action.payload
    },
    updateToggleServiceRequest: state => {
      state.toggleServiceRequest = !state.toggleServiceRequest
    },
    updateSelectedServiceRequest: (state, action) => {
      state.selectedServiceRequest = action.payload
    },
    updateProductRatings: (state, action) => {
      state.productRatings.push(action.payload)
    },
    resetProductRatings: state => {
      state.productRatings = []
    },
  },
})

export const {
  updateCopyPartyInformation,
  updateSelectedProjectDetails,
  updateLineDetails,
  updateOrderLineNumbers,
  updateSelectedOrderLineNumber,
  updateCertDetailsFormValues,
  updateCertDetailsFlatAttrList,
  updateProdDetailsModelList,
  updateProdDetailsRemarks,
  updateProdDetailsAttachments,
  updateProductCategoryOptions,
  updateStandardValues,
  updateSecondaryStandardValues,
  updateStandardSearch,
  updateStandardOptions,
  updateSecondaryStandardOptions,
  updateModelDetails,
  updateCertificateNumber,
  updatePartySiteNumber,
  updateAutoTimestampTask,
  updateIsCertNumberOnMaxlength,
  updateCertificateScheme,
  updateModelList,
  updateHasExistingModels,
  updateProductOwner,
  updateProductOwnerSearch,
  appendProductStandards,
  setModelsProductStandardsUpdates,
  updateHasEmptyProductStandardRow,
  updateProductCategoryStandards,
  updateHasCertificateProductStandard,
  updateAttachmentList,
  updateSchemeEcmType,
  updateSelectedProductCategory,
  updateRecommendationStatus,
  updateSkipCoDecisioning,
  updateIsCo,
  updateTempStandardOptions,
  updateIsTempStandardOptionLoaded,
  updateTempSecondaryStandardOptions,
  updateHasIssueDate,
  updateIsRPAttachmentValid,
  updateIsModelOnEdit,
  updateCopyState,
  updateIsAddNewCertFromParams,
  updateAssetDocuments,
  clearAssetDocuments,
  deleteAssetDocuments,
  removeAssetDocument,
  applyAssetDocumentSaveResults,
  updateCertificateAction,
  updateServiceRequestNumber,
  updateToggleServiceRequest,
  updateSelectedServiceRequest,
  updateProductRatings,
  resetProductRatings,
} = createCertificateSlice.actions

export const selectCopyPartyInformation = state => state.createCertificate.copyPartyInformation
export const selectValues = state => state.createCertificate.value
export const selectCertDetailValues = state => state.createCertificate.certDetails
export const selectProdDetailValues = state => state.createCertificate.productDetails
export const selectProdDetailAttachmentValues = state =>
  state.createCertificate.productDetails?.attachments
export const selectModelDetailValues = state => state.createCertificate.modelDetails
export const selectCertificateNumber = state => state.createCertificate.certificateNumber
export const selectPartySiteNumber = state => state.createCertificate.partySiteNumber
export const selectOwnerReference = state =>
  state.createCertificate.certDetails?.formValues?.[
    getAttributeUniqueName(CertificateGroupEnum.Certificate, 'ownerReference')
  ]
export const selectCertDetailFormValues = state => state.createCertificate.certDetails?.formValues
export const selectAutotimestampTask = state => state.createCertificate.autoTimestampTask
export const selectIsCertNumberOnMaxlength = state =>
  state.createCertificate.isCertNumberOnMaxlength
export const selectCertificateScheme = state => state.createCertificate.certificateScheme
export const selectModelList = state => state.createCertificate.modelList
export const selectHasExistingModels = state => state.createCertificate.hasExistingModels
export const selectProductOwner = state => state.createCertificate.productOwner
export const selectModelsProductStandardsUpdates = state =>
  state.createCertificate.modelsProductStandardsUpdates
export const selectStandardValue = state => state.createCertificate.certDetails.standardValue
export const selectSecondaryStandardValue = state =>
  state.createCertificate.certDetails.secondaryStandardValue
export const selectStandardSearch = state => state.createCertificate.certDetails.standardSearch
export const selectStandardOptions = state => state.createCertificate.certDetails.standardOptions
export const selectSecondaryStandardOptions = state =>
  state.createCertificate.certDetails.secondaryStandardOptions
export const selectProductCategoryStandards = state =>
  state.createCertificate.certDetails.productCategoryStandards
export const selectHasEmptyProductStandardRow = state =>
  state.createCertificate.hasEmptyProductStandardRow
export const selectHasCertificateProductStandard = state =>
  state.createCertificate.hasCertificateProductStandard
export const selectAttachmentUpdatedList = state => state.createCertificate.attachmentUpdatedList
export const selectSchemeEcmType = state => state.createCertificate.schemeEcmType
export const selectProductCategoryOptions = state =>
  state.createCertificate.certDetails.productCategoryOptions
export const selectSelectedProductCategory = state =>
  state.createCertificate.certDetails.selectedProductCategory
export const selectRecommendationStatus = state =>
  state.createCertificate.certDetails.isCertificateRecommended

export const selectSkipCoDecisioning = state =>
  state.createCertificate.certDetails.isSkipCoDecisioning
export const selectIsCo = state => state.createCertificate.certDetails.isCo
export const selectTempStandardOptions = state =>
  state.createCertificate.certDetails.tempStandardOptions
export const selectIsTempStandardOptionsLoaded = state =>
  state.createCertificate.certDetails.isTempStandardOptionsLoaded
export const selectTempSecondaryStandardOptions = state =>
  state.createCertificate.certDetails.tempSecondaryStandardOptions
export const selectHasIssueDate = state => state.createCertificate.certDetails.hasIssueDate
export const selectIsRPAttachmentValid = state => state.createCertificate.isRPAttachmentValid
export const selectIsModelOnEdit = state => state.createCertificate.isModelOnEdit
export const selectedCertHasSecondary = state =>
  Boolean(
    state.createCertificate.certDetails?.flatAttributeList?.some(
      a =>
        a.attribute_unique_name ===
        getAttributeUniqueName(CertificateGroupEnum.Certificate, 'secondaryStandard')
    )
  )
export const selectTestReportIssueDate = state =>
  state.createCertificate.certDetails?.formValues?.[
    getAttributeUniqueName(CertificateGroupEnum.Certificate, 'testReportIssueDate')
  ]
export const selectCopyState = state => state.createCertificate.copyState
export const selectIsStandardRequired = state =>
  Boolean(
    state.createCertificate.certDetails?.flatAttributeList?.find(
      x =>
        x.attribute_unique_name ===
        getAttributeUniqueName(CertificateGroupEnum.Certificate, 'standard')
    )?.is_required
  )

export const selectIsSecondaryStandardRequired = state =>
  Boolean(
    state.createCertificate.certDetails?.flatAttributeList?.find(
      x =>
        x.attribute_unique_name ===
        getAttributeUniqueName(CertificateGroupEnum.Certificate, 'secondaryStandard')
    )?.is_required
  )

export const selectIsAddNewCertFromParams = state => state.createCertificate.isAddNewCertFromParams

export const selectCertificateSchemeFromFormValues = state =>
  state.createCertificate.certDetails?.formValues?.certificate_certificateType

export const selectCertificateSubprogram = state =>
  state.createCertificate.certDetails?.formValues?.certificate_mark

export const selectAssetDocuments = state => state.createCertificate.assetDocuments

export const selectCertificateAction = state => state.createCertificate.certificateAction

export const selectCertDetailFlatAttribute = state =>
  state.createCertificate.certDetails?.flatAttributeList

export const selectServiceRequestNumber = state =>
  state.createCertificate.serviceRequestDetails?.serviceRequestNumber

export const selectToggleServiceRequest = state => state.createCertificate?.toggleServiceRequest

export const selectSelectedServiceRequest = state => state.createCertificate?.selectedServiceRequest

export const selectProductRatings = state => state.createCertificate.productRatings

export const selectProductOwnerSearch = state => state.createCertificate.productOwnerSearch

export default createCertificateSlice.reducer
